import React, { useState } from "react";
import { KeyRound, Loader2, CheckCircle2, ArrowRightLeft, HardDrive, Wand, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ReceiveBox } from "@/components/settings/ProjectTransfer";
import { LocalFolderImport } from "@/components/ingest/LocalFolderImport";

export const WELCOME_FLAG = "ws_welcome_done";

const Card = ({ n, title, done, children, id }) => (
  <div className={`studio-card p-5 space-y-3 ${done ? "opacity-80" : ""}`} data-testid={id}>
    <div className="flex items-center gap-3">
      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-mono ${done ? "bg-[#10B981] text-[#04120c]" : "border border-[#E6B04E] text-[#E6B04E]"}`}>
        {done ? <CheckCircle2 size={15} /> : n}</span>
      <span className="font-head font-bold">{title}</span>
    </div>
    {children}
  </div>
);

const KeyStep = ({ ok, setOk }) => {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.saveDesktopSettings({ emergent_llm_key: key.trim() });
      const h = await api.aiHealth("gemini");
      if (h.ok) { setOk(true); toast.success("AI key works"); } else toast.error(`Key saved but the test failed: ${h.detail}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Card n={1} title="Connect the AI" done={ok} id="welcome-step-key">
      <p className="text-[#94A3B8] text-xs">Paste your Emergent universal key. It powers clip tagging, the story and the edit plan. Transcription and faces run on this PC.</p>
      <div className="flex gap-2">
        <input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="sk-emergent-…" className="studio-input font-mono text-sm flex-1" data-testid="welcome-key-input" />
        <button className="btn btn-gold !py-1.5 text-sm" onClick={save} disabled={busy || !key.trim()} data-testid="btn-welcome-key-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}Save & test</button>
      </div>
    </Card>
  );
};

const FolderStep = ({ onReady }) => {
  const { selectProject, refreshProjects } = useApp();
  const [name, setName] = useState("");
  const [pid, setPid] = useState(null);
  const create = async () => {
    try { const p = await api.createProject({ name: name.trim() }); setPid(p.id); selectProject(p.id); refreshProjects(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (pid) return <LocalFolderImport projectId={pid} onDone={() => onReady(pid)} />;
  return (
    <div className="flex gap-2">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Project name, e.g. Ananya & Rohan" className="studio-input text-sm flex-1" data-testid="welcome-project-name" />
      <button className="btn btn-gold !py-1.5 text-sm" onClick={create} disabled={!name.trim()} data-testid="btn-welcome-create-project"><HardDrive size={14} />Create & pick folder</button>
    </div>
  );
};

export default function Welcome() {
  const nav = useNavigate();
  const { selectProject, refreshProjects } = useApp();
  const [keyOk, setKeyOk] = useState(false);
  const [mode, setMode] = useState("transfer");
  const [ready, setReady] = useState(null);

  const finish = (pid) => {
    localStorage.setItem(WELCOME_FLAG, "1");
    if (pid) { selectProject(pid); refreshProjects(); nav("/wizard"); } else nav("/");
  };

  return (
    <div className="p-8 max-w-4xl space-y-5 fade-up" data-testid="welcome-page">
      <div>
        <h1 className="font-head text-4xl font-bold flex items-center gap-3"><Sparkles className="text-[#E6B04E]" size={30} />Welcome to AI Wedding Studio</h1>
        <p className="text-[#94A3B8] text-sm mt-1">Three quick steps and you're editing. Everything runs on this PC, with no upload limits.</p>
      </div>
      <KeyStep ok={keyOk} setOk={setKeyOk} />
      <Card n={2} title="Bring in a wedding" done={!!ready} id="welcome-step-wedding">
        <div className="flex gap-2">
          <button className={`btn !py-1 text-xs ${mode === "transfer" ? "btn-gold" : "btn-ghost"}`} onClick={() => setMode("transfer")} data-testid="welcome-mode-transfer"><ArrowRightLeft size={13} />Transfer code from the web app</button>
          <button className={`btn !py-1 text-xs ${mode === "folder" ? "btn-gold" : "btn-ghost"}`} onClick={() => setMode("folder")} data-testid="welcome-mode-folder"><HardDrive size={13} />Footage folder on this PC</button>
        </div>
        {mode === "transfer" ? <ReceiveBox onProject={setReady} /> : <FolderStep onReady={setReady} />}
      </Card>
      <Card n={3} title="Start editing" done={false} id="welcome-step-edit">
        <p className="text-[#94A3B8] text-xs">The Edit Wizard picks up where the project left off. Transferred rough cuts, reviews and Deep AI results carry over.</p>
        <div className="flex gap-2">
          <button className="btn btn-gold" onClick={() => finish(ready)} disabled={!ready} data-testid="btn-welcome-open-wizard"><Wand size={15} />Open the Edit Wizard</button>
          <button className="btn btn-ghost" onClick={() => finish(null)} data-testid="btn-welcome-skip">Skip for now</button>
        </div>
      </Card>
    </div>
  );
}
